// Converts a parsed VIS match (the JSON object produced by VISFile's
// `visToJson` / `loadVISAsJson`) into the data shape consumed by
// `buildDVWMatchText`, so a SportsStreamLab / VolleyStation `.vis` file can be
// turned into DataVolley scout text and opened through the normal
// `generateMatch` pipeline.
//
// The heart of the conversion is decoding each rally's `rally_code` (the VIS
// `CMD` field). A rally_code is the sequence of touches in a rally, with each
// team's possession separated by a "/". Possessions alternate, starting with
// the serving team. Within a possession every touch is a skill letter,
// optionally followed by the player's shirt number and an evaluation marker:
//
//   V  serve        R  receive      S  set (by the setter)
//   A  attack       D  dig          B  block        F  freeball
//   SS set by a non-setter (still a "set")
//   DPF dig sent straight over the net like a freeball (still a "dig")
//   +  kill / point won / best evaluation        -  error
//
// Example: "V14/R12/A21/DS20A12/DSSA19/D22S20+A18+" (Team A serving) decodes to
//   A: serve 14 | B: receive 12 | A: attack 21 | B: dig, set 20, attack 12 |
//   A: dig, set (non-setter), attack 19 | B: dig 22, set 20 (good), attack 18 (kill)

import { generateUUID } from "./Utils";

// VIS skill letter -> DataVolley skill code.
//   DataVolley: S=Serve R=Reception E=Set A=Attack B=Block D=Dig F=Freeball
const VIS_TO_DVW_SKILL = {
  V: "S",
  R: "R",
  S: "E",
  A: "A",
  D: "D",
  B: "B",
  F: "F",
};

// "19:,14:,16:,7:,21:,12:" -> [19,14,16,7,21,12] (six on-court shirt numbers in
// court-position order 1..6; the part after ":" carries libero pairings which
// we ignore here).
function parsePairs(s) {
  if (!s) return [];
  return String(s)
    .split(",")
    .map((x) => x.split(":")[0].trim())
    .filter((x) => x !== "")
    .map((x) => parseInt(x, 10))
    .filter((n) => !Number.isNaN(n));
}

// Decodes a single possession segment into DataVolley scout events for `team`.
function parseSegment(seg, team, out) {
  let i = 0;
  while (i < seg.length) {
    // Read the skill token, longest match first (DPF, SS, then single letters).
    let dvw;
    if (seg.startsWith("DPF", i)) {
      dvw = "D"; // dig sent over like a freeball
      i += 3;
    } else if (seg.startsWith("SS", i)) {
      dvw = "E"; // set performed by a non-setter
      i += 2;
    } else {
      dvw = VIS_TO_DVW_SKILL[seg[i]];
      i += 1;
    }

    // Read the optional shirt number.
    let num = "";
    while (i < seg.length && seg[i] >= "0" && seg[i] <= "9") {
      num += seg[i];
      i += 1;
    }

    // Read the optional evaluation marker.
    let mark = null;
    if (i < seg.length && (seg[i] === "+" || seg[i] === "-")) {
      mark = seg[i];
      i += 1;
    }

    // Unknown skill letter, or a touch with no recorded player: skip it (we have
    // already consumed its number/marker so parsing stays in sync).
    if (!dvw || num === "") {
      continue;
    }

    // VIS "+" (best/kill) -> DataVolley "#" (perfect/point); VIS "-" (error) ->
    // "=" (error/point lost); neutral touch -> "+" (positive, keeps play alive).
    const evaluation = mark === "+" ? "#" : mark === "-" ? "=" : "+";

    out.push({
      team,
      skill: dvw,
      shirtNumber: parseInt(num, 10),
      evaluation,
    });
  }
}

// Decodes a rally_code into an ordered list of scout events. Possessions are
// separated by "/" and alternate teams, starting with the serving team.
export function parseRallyCode(code, serveTeam) {
  const events = [];
  if (!code) return events;
  const other = serveTeam === "A" ? "B" : "A";
  String(code)
    .split("/")
    .forEach((seg, idx) => {
      parseSegment(seg, idx % 2 === 0 ? serveTeam : other, events);
    });
  return events;
}

// Maps a VIS roster (array of player dicts) to DataVolley player objects.
// full_name is split: the first whitespace-delimited token becomes LastName and
// the remainder becomes FirstName. The short `name` field becomes NickName.
function mapRoster(roster) {
  return (roster || []).map((p) => {
    const tokens = String(p.full_name || "").trim().split(/\s+/).filter(Boolean);
    const lastName = tokens[0] || p.name || `#${p.number}`;
    const firstName = tokens.slice(1).join(" ");
    return {
      shirtNumber: p.number,
      FirstName: firstName,
      LastName: lastName,
      NickName: p.name || "",
      role1: p.is_setter ? "S" : p.is_libero ? "L" : "",
    };
  });
}

// Picks a team's primary setter shirt: a flagged setter who is on court in the
// opening rally, else the first flagged setter, else null.
function pickSetter(roster, onCourt) {
  const setters = (roster || []).filter((p) => p.is_setter).map((p) => p.number);
  const found = setters.find((n) => onCourt.includes(n));
  return found ?? setters[0] ?? null;
}

// Converts the parsed VIS JSON into the object consumed by buildDVWMatchText.
export function visJsonToMatchData(vis) {
  const teamA = vis.team_a || {};
  const teamB = vis.team_b || {};
  const rallies = vis.rally_events || [];

  const firstRally = rallies[0] || {};
  const firstA = parsePairs(firstRally.a_cur_player_pairs);
  const firstB = parsePairs(firstRally.b_cur_player_pairs);

  const setterA = pickSetter(teamA.roster, firstA);
  const setterB = pickSetter(teamB.roster, firstB);

  const liberoA = (teamA.roster || [])
    .filter((p) => p.is_libero)
    .map((p) => p.number);
  const liberoB = (teamB.roster || [])
    .filter((p) => p.is_libero)
    .map((p) => p.number);

  const sets = (vis.sets || []).map((s) => {
    const setRallies = rallies.filter((r) => r.set_num === s.set_num);
    const events = [];
    let lineupA = null;
    let lineupB = null;

    setRallies.forEach((r) => {
      const serveTeam = r.serve_team_code === 1 ? "A" : "B";
      const A = parsePairs(r.a_cur_player_pairs);
      const B = parsePairs(r.b_cur_player_pairs);
      if (!lineupA && A.length === 6) lineupA = A;
      if (!lineupB && B.length === 6) lineupB = B;

      const snapshot = {
        A: A.slice(0, 6).map(String),
        B: B.slice(0, 6).map(String),
      };

      // The rally's start time stamps the first event; every later event in the
      // same rally is bumped by one second so the events stay ordered in time.
      const rallyStart = r.rally_start_time ? new Date(r.rally_start_time) : null;

      const rallyEvents = parseRallyCode(r.rally_code, serveTeam);

      rallyEvents.forEach((ev, i) => {
        ev.LineupSnapshot = snapshot;
        ev.scoreA = r.score_a ?? 0;
        ev.scoreB = r.score_b ?? 0;
        if (rallyStart && !Number.isNaN(rallyStart.getTime())) {
          ev.TimeStamp = new Date(rallyStart.getTime() + i * 1000);
        }
        events.push(ev);
      });
    });

    return {
      pointsA: s.score_a ?? 0,
      pointsB: s.score_b ?? 0,
      lineupA: (lineupA || []).map((n, i) => ({ shirtNumber: n, position: i + 1 })),
      lineupB: (lineupB || []).map((n, i) => ({ shirtNumber: n, position: i + 1 })),
      events,
    };
  });

  const comp = vis.competition || {};
  const match = vis.match || {};

  return {
    teamAName: teamA.full_name || teamA.short_name || "Team A",
    teamBName: teamB.full_name || teamB.short_name || "Team B",
    tournamentName: comp.title || comp.phase || comp.group || "",
    date: match.scheduled_datetime ? new Date(match.scheduled_datetime) : new Date(),
    guid: match.match_id ? String(match.match_id) : generateUUID(),
    rosterA: mapRoster(teamA.roster),
    rosterB: mapRoster(teamB.roster),
    setterA,
    setterB,
    liberoA,
    liberoB,
    sets,
  };
}
